import { memo } from "react";
import {
  LoginConnState,
  SESSION_STATUS,
  TRADING_ENV,
  gateAction,
  isReduceOnly,
  useConnection,
  useTrading,
} from "@helm/core";
import { useGateContext } from "./control";

/**
 * Environment badge on the top bar, the order pad and the DOM (ADR-0021, ADR-0027).
 *
 * - MOCK / TEST: amber "SIMULATED — mock / Rithmic Test".
 * - PAPER (paper-trading and prop-firm systems): violet configured trading-switch state.
 * - LIVE: solid red "LIVE — REAL MONEY" / "LIVE — TRADING OFF".
 * - Anything else: grey "TRADING OFF" with the reason.
 *
 * The environment and switch come from gateway state, never from the client. A saved mode is
 * not current connectivity or a promise that an action will execute.
 */
export const SimBadge = memo(function SimBadge({
  compact = false,
}: {
  compact?: boolean;
}): React.JSX.Element {
  const ctx = useGateContext();
  const caughtUp = useConnection((c) => c.financeReady);
  const login = useTrading((t) => t.login);
  const sessionEnv = ctx.session?.environment;
  // Before its order plant reports (or after it failed), a login's SessionState says UNSPECIFIED:
  // label it from the connected login instead (ADR-0027).
  const loginEnv: number | undefined =
    login?.state === LoginConnState.CONNECTED ? login.environment : undefined;
  const sessionIsKnown =
    sessionEnv === TRADING_ENV.MOCK ||
    sessionEnv === TRADING_ENV.TEST ||
    sessionEnv === TRADING_ENV.PAPER ||
    sessionEnv === TRADING_ENV.LIVE;
  const env = sessionIsKnown ? sessionEnv : loginEnv;
  const on = sessionIsKnown && ctx.session?.tradingEnabled === true;
  const reduceOnly = isReduceOnly(ctx.session);
  const g = gateAction(ctx, { kind: "flattenAll" });
  const readiness = !ctx.gatewayConnected
    ? "OFFLINE"
    : !caughtUp
      ? "SYNCING"
      : ctx.session?.status !== SESSION_STATUS.READY
        ? "NOT READY"
        : null;
  const connection = !ctx.gatewayConnected
    ? "Gateway disconnected."
    : !caughtUp
      ? "Gateway connected; session state is catching up."
      : readiness
        ? "Gateway connected; order plant not ready."
        : "Gateway connected; order plant ready.";
  // A Flatten refusal is not a verdict on cancels, which can have different readiness rules.
  // Keep account identifiers out of this shared badge, including when privacy mode is enabled.
  const flatten = g.ok
    ? ""
    : g.code === "POSITION_UNCONFIRMED"
      ? " Exit all accts unavailable: positions are unconfirmed."
      : " Exit all accts currently unavailable; its control shows the refusal reason.";
  if (env === TRADING_ENV.PAPER || env === TRADING_ENV.LIVE) {
    const live = env === TRADING_ENV.LIVE;
    const what = live ? "LIVE" : compact ? "PROP" : "PAPER / PROP";
    // Real money is never abbreviated, compact or not (owner 2026-10-04: the status line is the
    // one place the environment shows once the module badges went).
    const state = on
      ? live
        ? "REAL MONEY"
        : "TRADING ON"
      : sessionIsKnown
        ? "TRADING OFF"
        : "TRADING UNAVAILABLE";
    return (
      <span
        className={`sim-badge ${live ? "env-badge-live" : "env-badge-paper"}${on ? " env-badge-on" : ""}`}
        role="status"
        data-environment={live ? "live" : "paper"}
        data-trading={on ? "on" : reduceOnly ? "reduce-only" : "off"}
        title={`Configured destination: Rithmic ${live ? "LIVE (real money)" : "paper / prop-firm"}. ${connection} ${
          !sessionIsKnown
            ? 'The "Allow trading" switch has not been reported for this order plant.'
            : on
              ? 'The "Allow trading" switch is ON; each action remains subject to its checks.'
              : 'The "Allow trading" switch is OFF: new orders are refused. Exits and cancels are checked separately for availability.'
        }${flatten} Manage the switch in Settings → Rithmic.`}
      >
        {what} — {state}
        {readiness ? ` · ${readiness}` : ""}
      </span>
    );
  }
  if (env === TRADING_ENV.MOCK || env === TRADING_ENV.TEST) {
    const where = env === TRADING_ENV.MOCK ? "mock" : "Rithmic Test";
    return (
      <span
        className="sim-badge"
        role="status"
        data-environment={where}
        title={`Configured destination: ${env === TRADING_ENV.MOCK ? "the local rithmic-mock" : "Rithmic Test"} (simulated). ${connection} Each action remains subject to its checks.${flatten}`}
      >
        {compact ? "SIM" : "SIMULATED"} — {where}
        {readiness ? ` · ${readiness}` : ""}
      </span>
    );
  }
  return (
    <span
      className="sim-badge sim-badge-off"
      role="status"
      data-environment="off"
      title={g.ok ? "" : g.reason}
    >
      TRADING OFF
    </span>
  );
});
